/**
 * QuotaUsageTable Component
 *
 * Table view showing detailed quota usage for each pool and model.
 * Supports filtering by provider/pool and sorting by quota type.
 */

import React, { useState, useMemo } from 'react';
import { clsx } from 'clsx';
import { QuotaProgressBar } from './QuotaProgressBar';
import { StatusBadge } from '../shared/StatusBadge';
import type { 
  QuotaUsageResponseNew, 
  ModelHealthResponse
} from '../../../services/llmAdminApi';

export interface QuotaUsageTableProps {
  quotaData: QuotaUsageResponseNew;
  healthData: ModelHealthResponse;
  onReset: (provider_name: string, pool_index: number, model_use?: string, quota_type?: string) => void;
}

type SortField = 'provider' | 'pool_index' | 'model_use' | 'quota_type' | 'used' | 'percentage';
type SortDirection = 'asc' | 'desc';

const quotaTypeLabels: Record<string, string> = {
  concurrency: '并发数',
  token_per_minute: 'Token/分钟',
  token_per_hour: 'Token/小时',
  token_per_day: 'Token/天',
  token_per_month: 'Token/月',
  token_total: 'Token总量',
  request_per_minute: '请求/分钟',
  request_per_hour: '请求/小时',
  request_per_day: '请求/天',
  request_per_month: '请求/月',
  request_total: '请求总量',
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const formatResetTime = (resetAt: string | null): string => {
  if (!resetAt) return '-';
  try {
    const date = new Date(resetAt);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    if (diffMs <= 0) return '已重置';
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}分钟`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}小时`;
    return `${Math.floor(diffHours / 24)}天`;
  } catch {
    return '-';
  }
};

export const QuotaUsageTable: React.FC<QuotaUsageTableProps> = ({
  quotaData,
  healthData,
  onReset,
}) => {
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [poolFilter, setPoolFilter] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('provider');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const uniqueProviders = useMemo(() => {
    return Array.from(new Set(quotaData.usage.map((item) => item.provider_name))).sort();
  }, [quotaData]);

  const uniquePools = useMemo(() => {
    if (!providerFilter) return [];
    return Array.from(
      new Set(
        quotaData.usage
          .filter((item) => item.provider_name === providerFilter)
          .map((item) => item.pool_index.toString())
      )
    ).sort();
  }, [quotaData, providerFilter]);

  const filteredData = useMemo(() => {
    let data = quotaData.usage;

    if (providerFilter) {
      data = data.filter((item) => item.provider_name === providerFilter);
    }

    if (poolFilter) {
      data = data.filter((item) => item.pool_index.toString() === poolFilter);
    }

    data.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'provider':
          comparison = a.provider_name.localeCompare(b.provider_name);
          break;
        case 'pool_index':
          comparison = a.pool_index - b.pool_index;
          break;
        case 'model_use':
          comparison = a.model_use.localeCompare(b.model_use);
          break;
        case 'quota_type':
          comparison = a.quota_type.localeCompare(b.quota_type);
          break;
        case 'used':
          comparison = a.used_count - b.used_count;
          break;
        case 'percentage':
          const aPct = a.percentage || 0;
          const bPct = b.percentage || 0;
          comparison = aPct - bPct;
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return data;
  }, [quotaData, providerFilter, poolFilter, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getHealthStatus = (providerName: string, poolIndex: number, modelUse: string) => {
    return healthData.models.find(
      (m) =>
        m.provider_name === providerName &&
        m.pool_index === poolIndex &&
        m.model_use === modelUse
    );
  };

  const getAlertVariant = (percentage: number | null): 'success' | 'warning' | 'error' | 'neutral' => {
    if (percentage === null) return 'neutral';
    if (percentage >= 100) return 'error';
    if (percentage >= 80) return 'warning';
    if (percentage >= 70) return 'warning';
    return 'success';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">配额使用详情</h4>
        <div className="flex items-center gap-3">
          <select
            value={providerFilter}
            onChange={(e) => {
              setProviderFilter(e.target.value);
              setPoolFilter('');
            }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">全部提供商</option>
            {uniqueProviders.map((provider) => (
              <option key={provider} value={provider}>
                {provider}
              </option>
            ))}
          </select>

          {providerFilter && (
            <select
              value={poolFilter}
              onChange={(e) => setPoolFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">全部池</option>
              {uniquePools.map((pool) => (
                <option key={pool} value={pool}>
                  Pool #{pool}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {filteredData.length === 0 ? (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 text-sm">
          暂无配额数据
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th
                  className="px-4 py-3 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('provider')}
                >
                  提供商
                  {sortField === 'provider' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th
                  className="px-4 py-3 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('pool_index')}
                >
                  池索引
                  {sortField === 'pool_index' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th
                  className="px-4 py-3 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('model_use')}
                >
                  模型用途
                  {sortField === 'model_use' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th
                  className="px-4 py-3 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('quota_type')}
                >
                  配额类型
                  {sortField === 'quota_type' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th
                  className="px-4 py-3 text-right font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('used')}
                >
                  已使用
                  {sortField === 'used' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">限制</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">剩余</th>
                <th className="px-4 py-3 text-center font-medium text-gray-700">进度</th>
                <th
                  className="px-4 py-3 text-center font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('percentage')}
                >
                  百分比
                  {sortField === 'percentage' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th className="px-4 py-3 text-center font-medium text-gray-700">状态</th>
                <th className="px-4 py-3 text-center font-medium text-gray-700">重置时间</th>
                <th className="px-4 py-3 text-center font-medium text-gray-700">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.map((item, index) => {
                const rowKey = `${item.provider_name}-${item.pool_index}-${item.model_use}-${item.quota_type}`;
                const health = getHealthStatus(item.provider_name, item.pool_index, item.model_use);
                const alertVariant = getAlertVariant(item.percentage);

                return (
                  <tr key={rowKey} className={clsx('hover:bg-gray-50', index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}>
                    <td className="px-4 py-3 text-gray-800">{item.provider_name}</td>
                    <td className="px-4 py-3 text-gray-800">#{item.pool_index}</td>
                    <td className="px-4 py-3 text-gray-800">{item.model_use}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {quotaTypeLabels[item.quota_type] || item.quota_type}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-800 font-medium">
                      {formatNumber(item.used_count)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {item.limit_value ? formatNumber(item.limit_value) : '无限制'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {item.remaining ? formatNumber(item.remaining) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <QuotaProgressBar
                        used={item.used_count}
                        limit={item.limit_value}
                        size="sm"
                        showLabel={false}
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.percentage !== null ? (
                        <StatusBadge variant={alertVariant} size="sm">
                          {item.percentage.toFixed(1)}%
                        </StatusBadge>
                      ) : (
                        <span className="text-gray-500 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {health && (
                        <StatusBadge
                          variant={health.available ? 'success' : 'error'}
                          size="sm"
                          dot
                        >
                          {health.available ? '可用' : `不可用(${health.error_count})`}
                        </StatusBadge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600 text-xs">
                      {formatResetTime(item.reset_at)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => onReset(
                          item.provider_name,
                          item.pool_index,
                          item.model_use,
                          item.quota_type
                        )}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        重置
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>共 {filteredData.length} 条记录</span>
        <span>总记录数: {quotaData.total}</span>
      </div>
    </div>
  );
};

export default QuotaUsageTable;